'use client';

import Link from 'next/link';
import { ArrowLeft, ArrowRight, Info, MapPin, ShieldCheck, Tag } from 'lucide-react';
import { getCategoryUrl, getCityUrl, getPlaceCityShelfUrl } from '@/utils/slug';

import { LocalBusinessStructuredData, BreadcrumbStructuredData } from '@/components/directory/StructuredData';
import RelatedStores from '@/components/directory/RelatedStores';
import ChainLocationsStrip from '@/components/directory/ChainLocationsStrip';
import DirectoryPhotoGalleryDisplay from '@/components/directory/DirectoryPhotoGalleryDisplay';
import { showsSeedAuthoredSurface } from '@/lib/place-photo-gallery';
import GoogleMapEmbed from '@/components/shared/GoogleMapEmbed';
import StoreViewTracker from '@/components/tracking/StoreViewTracker';
import BusinessHoursCollapsible from '@/components/storefront/BusinessHoursCollapsible';
import ContactInformationCollapsible from '@/components/directory/ContactInformationCollapsible';
import { TenantQRCode } from '@/components/public/TenantQRCode';
import LastViewed from '@/components/directory/LastViewed';
import SuggestBusinessCta from '@/components/directory/SuggestBusinessCta';
import UnclaimedDirectoryBanner from '@/components/directory/UnclaimedDirectoryBanner';
import AddBusinessCta from '@/components/directory/AddBusinessCta';
import HoursStatusBadge from '@/components/storefront/HoursStatusBadge';
import CouponSpotlight from '@/components/storefront/CouponSpotlight';
import FlyerSection from '@/components/storefront/FlyerSection';
import PublicInquiryForm from '@/components/crm/PublicInquiryForm';
import { MarketIntelBanner } from '@/components/place/MarketIntelBanner';
import { MarketIntelSidebar } from '@/components/place/MarketIntelSidebar';
import ShelfSlotsPreview from '@/components/place/ShelfSlotsPreview';

import { useQrScanTracking } from '@/hooks/useQrScanTracking';
import { useDirectoryPresenceTracking } from '@/hooks/useDirectoryPresenceTracking';
import type { DirectoryEntryOptionsState } from '@/services/CapabilityResolutionService';
import type { MarketIntelTeaserSummary } from '@/services/MarketIntelPublicService';
import type { ReferrerShelf, SecondaryShelf } from '@/lib/place-shelves';

/**
 * PlaceEntryEditorialLayout — editorial-style layout adapted for directory
 * presence seeds (unclaimed listings from public information).
 *
 * Adapts the Editorial directory entry aesthetic with seed-appropriate content:
 * - Hero: dark gradient with business name + category, provenance disclaimer,
 *   and a "Claim this listing" CTA (instead of "Visit Storefront")
 * - Sidebar: Contact, Hours, Map, QR (the NAP+ data seeds actually have)
 * - Commerce sections (Featured Products, Categories, Coupons, Reviews, FAQ,
 *   Inquiry) are omitted — seeds have max_skus: 0 and no storefront
 * - Store photos kept — directory_photos is the one commerce-adjacent surface a
 *   seed legitimately carries, populated by the operator ahead of the claim
 * - Related Stores kept for shopper discovery
 * - Unclaimed banner at top
 *
 * Sidebar visibility is driven by DirectoryEntryOptionsState (tier capability
 * resolution: directory_entry_hours_on, directory_entry_map_on,
 * directory_entry_contact_on, directory_entry_qr_on) — NOT by storefront
 * merchant preferences, which don't apply to unclaimed seed listings.
 */
export interface PlaceEntryEditorialLayoutProps {
  tenantId: string;
  /** Public listing slug — used for Layer 3 engagement tracking. */
  slug: string;
  listing: any;
  businessHours: any;
  hoursStatus: any;
  tenantInfo: any;
  slugForRelated: string;
  dirEntryOpts: DirectoryEntryOptionsState | null;
  showsHours: boolean;
  showsMap: boolean;
  showsLocation: boolean;
  showsContact: boolean;
  showsQr: boolean;
  showsGallery: boolean;
  currentUrl: string;
  baseUrl: string;
  fullAddress: string;
  claimToken?: string | null;
  publicDisclaimer?: string | null;
  /** Tier-C-safe audit narrative — preferred About-section source (§1.1). */
  publicNarrative?: string | null;
  /** Server-rendered market-intel teaser — feeds the sponsored slot under Contact. */
  marketIntelTeaser?: MarketIntelTeaserSummary | null;
  /** Resolved `?shelf=` referrer — the shelf the shopper clicked through from.
   *  When set, the back link returns to it and a place-category referrer's
   *  label leads the hero subtitle (the card showed the shelf name, so the
   *  entry keeps that context; the primary remains a browse link). */
  referrerShelf?: ReferrerShelf | null;
  /** Secondary categories resolved to live shelves — rendered as "Also on"
   *  chips so the page shows every shelf the seed is filed on. */
  secondaryShelves?: SecondaryShelf[];
}

/**
 * Map the listing's public `shelfItems` (sanitized projection of the seed's
 * observed_assets — emitted by GET /api/directory/:slug) into preview items.
 * The tag is honest framing ("Shoppers mention"/"Commonly carried") derived
 * from where the item was observed — never a stock/price claim.
 */
function mapShelfItems(
  shelfItems: Array<{ name: string; evidence?: string | null; observedOn?: string | null; confidence?: string | null }> | null | undefined,
): { name: string; tag: string; evidence?: string | null }[] | undefined {
  if (!Array.isArray(shelfItems) || shelfItems.length === 0) return undefined;
  const tagFor = (it: { observedOn?: string | null }) =>
    it.observedOn === 'reviews' ? 'Shoppers mention' : 'Commonly carried';
  return shelfItems.map((it) => ({
    name: it.name,
    tag: tagFor(it),
    evidence: it.evidence ?? null,
  }));
}

export default function PlaceEntryEditorialLayout({
  tenantId,
  slug,
  listing,
  businessHours,
  hoursStatus,
  tenantInfo,
  slugForRelated,
  dirEntryOpts,
  showsHours,
  showsMap,
  showsLocation,
  showsContact,
  showsQr,
  showsGallery,
  currentUrl,
  baseUrl,
  fullAddress,
  claimToken,
  publicDisclaimer,
  publicNarrative,
  marketIntelTeaser,
  referrerShelf,
  secondaryShelves,
}: PlaceEntryEditorialLayoutProps) {
  useQrScanTracking(tenantId, 'directory');

  // Layer 3 — engagement events. Only track genuine seed listings (the /place
  // surface is seed-only; this gate mirrors the client-side listingOrigin gate).
  const { trackClaimClick, trackCallClick } = useDirectoryPresenceTracking({
    slug,
    active: listing.listingOrigin === 'directory_seed',
  });

  const primaryColor = tenantInfo?.metadata?.primaryColor || tenantInfo?.metadata?.primary_color || null;
  const hasClaimToken = !!claimToken;
  // Prefer the /c/{code} short path — it resolves to the seed's live token,
  // so the link survives token reissue; raw token URL is the fallback.
  const claimHref = listing.claimShortCode
    ? `/c/${listing.claimShortCode}`
    : hasClaimToken
      ? `/place/claim/${claimToken}`
      : '#claim-inquiry';
  const disclaimer = publicDisclaimer ||
    `${listing.businessName} is listed from public information (address and phone). This is not a claimed profile and may be incomplete.`;

  // Shelf-context display: the referring shelf label leads the subtitle when
  // the shopper arrived from a place-category shelf (the card they clicked
  // showed that shelf name); the primary category is the fallback.
  const displayCategory = referrerShelf?.categoryLabel || listing.primaryCategory;

  return (
    <>
      <LocalBusinessStructuredData
        listing={{
          ...listing,
          description: listing.description,
          sameAs: listing.sameAs,
          schemaType: listing.schemaTypeHint,
        }}
        url={currentUrl}
      />
      <BreadcrumbStructuredData items={[
        { name: 'Home', url: baseUrl },
        { name: 'Directory', url: `${baseUrl}/directory` },
        { name: listing.businessName, url: currentUrl },
      ]} />
      <StoreViewTracker tenantId={tenantId} storeName={listing.businessName} categories={listing.categories} listingOrigin="directory_seed" surface="place" />

      <div className="min-h-screen bg-white">
        {/* Editorial Hero — seed-adapted. Default palette is the /place violet
            family (the sibling of the directory entry's blue banner); a claimed
            tenant's own primaryColor still wins. */}
        <div
          className={`relative text-white overflow-hidden ${primaryColor ? '' : 'bg-gradient-to-br from-violet-700 via-violet-800 to-indigo-900'}`}
          style={primaryColor ? { background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}88 50%, ${primaryColor}33 100%)` } : undefined}
        >
          <div className="absolute inset-0 opacity-20">
            <div className="w-full h-full bg-gradient-to-br from-violet-400 to-indigo-600" />
          </div>
          <div className="relative max-w-5xl mx-auto px-6 py-16 sm:py-24">
            <Link
              href={referrerShelf?.href || '/directory'}
              className="inline-flex items-center text-sm text-white/70 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to {referrerShelf?.label || 'Directory'}
            </Link>
            {listing.primaryCategory && (
              <div className="-mt-4 mb-2">
                <Link
                  href={getCategoryUrl({ name: listing.primaryCategory })}
                  className="inline-flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors"
                >
                  <Tag className="w-4 h-4" />
                  Browse {listing.primaryCategory} stores
                  <span aria-hidden="true">→</span>
                </Link>
              </div>
            )}
            {listing.city && listing.state && (
              <div className="mb-6">
                <Link
                  href={getCityUrl(listing.city, listing.state)}
                  className="inline-flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white transition-colors"
                >
                  <MapPin className="w-4 h-4" />
                  Browse stores in {listing.city}, {listing.state}
                  <span aria-hidden="true">→</span>
                </Link>
              </div>
            )}
            {(secondaryShelves?.length ?? 0) > 0 && (
              <div className="-mt-2 mb-6 flex flex-wrap items-center gap-2 text-sm">
                <span className="text-white/60">Also on:</span>
                {secondaryShelves!.map((shelf) =>
                  shelf.href ? (
                    <Link
                      key={shelf.label}
                      href={shelf.href}
                      className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white/90 hover:text-white transition-colors"
                    >
                      {shelf.label}
                    </Link>
                  ) : (
                    <span
                      key={shelf.label}
                      className="inline-flex items-center px-3 py-1 rounded-full bg-white/5 text-white/50"
                    >
                      {shelf.label}
                    </span>
                  ),
                )}
              </div>
            )}
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                {listing.logoUrl && showsSeedAuthoredSurface(listing, dirEntryOpts?.canShowLogo) && (
                  <img src={listing.logoUrl} alt={listing.businessName} className="w-20 h-20 rounded-xl object-cover border-2 border-white/20" />
                )}
                <div>
                  <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">{listing.businessName}</h1>
                  {displayCategory && (
                    <p className="text-white/80 mt-2 text-lg">{displayCategory}</p>
                  )}
                  {!listing.isClaimed && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 mt-3 rounded-full bg-yellow-500/20 border border-yellow-500/40 text-sm font-medium text-yellow-300">
                      <span className="w-2 h-2 rounded-full bg-yellow-400" aria-hidden="true" />
                      Unclaimed listing
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                {listing.isClaimed ? null : hasClaimToken ? (
                  <a
                    href={claimHref}
                    onClick={trackClaimClick}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white text-neutral-900 rounded-lg hover:bg-neutral-100 transition-colors font-semibold"
                  >
                    <ShieldCheck className="w-5 h-5" /> Claim this listing
                  </a>
                ) : (
                  <a
                    href={claimHref}
                    onClick={trackClaimClick}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white text-neutral-900 rounded-lg hover:bg-neutral-100 transition-colors font-semibold"
                  >
                    <Info className="w-5 h-5" /> Are you the owner?
                  </a>
                )}
                {showsHours && hoursStatus && (
                  <HoursStatusBadge status={hoursStatus} size="lg" animate={true} />
                )}

              </div>
            </div>
          </div>
        </div>

        <section className="max-w-6xl mx-auto px-6 -mt-4 relative z-20 mb-4">
          <CouponSpotlight tenantId={tenantId} variant="banner" />
        </section>

        <FlyerSection tenantSlug={tenantId} tenantId={tenantId} surface="place" />

        {!listing.isClaimed && (
          <section className="max-w-6xl mx-auto px-6 relative z-20 mb-4">
            <UnclaimedDirectoryBanner businessName={listing.businessName} />
          </section>
        )}

        {/* Balanced content grid — map on left (wider), NAP data on right.
            NAP is the shopper's primary task, so it leads the page body;
            the claim/conversion blocks follow below. */}
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left — Location + Contact stacked */}
            <div className="lg:col-span-7 space-y-8">
              {/* Store photos — the shelves themselves. Reuses the directory
                  entry gallery component, which self-hides when a listing has
                  no photos, so it carries no heading here (same as the four
                  directory entry layouts). Addressed by slug: a seed tenant has
                  tenants.slug = null, so the photos API resolves the listing
                  row by its slug. */}
              {showsGallery && (
                <DirectoryPhotoGalleryDisplay
                  listing={{ id: slug, business_name: listing.businessName, is_directory_published: true }}
                  isPublished={true}
                />
              )}

              {showsMap && listing.address && (
                <div className="bg-neutral-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-4">Location</h3>
                  <GoogleMapEmbed address={listing.address} />
                </div>
              )}

              {showsContact && (
                <div className="bg-neutral-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-4">Contact</h3>
                  <ContactInformationCollapsible
                    tenant={listing}
                    fullAddress={showsLocation ? fullAddress : ''}
                    initialExpanded={true}
                    isRetailStore={true}
                    onPhoneClick={trackCallClick}
                  />
                </div>
              )}

              {/* Square sponsored slot (300x250) — seed pages only. Fills
                  the space under Contact when the Hours column runs taller.
                  The tall slot lives inside the Market Intel panel below,
                  so the two never collide. */}
              {listing.listingOrigin === 'directory_seed' && (
                <div className="flex justify-center">
                  <MarketIntelBanner
                    variant="square"
                    surfaceType="seed"
                    teaser={marketIntelTeaser}
                    seedId={listing.seedId}
                    reportShortCode={listing.claimShortCode}
                    qrStyle={listing.qrStyle}
                    logoUrl={listing.logoUrl}
                  />
                </div>
              )}
            </div>

            {/* Right — Hours standalone */}
            <div className="lg:col-span-5 space-y-8">
              {showsHours && businessHours && (
                <div className="bg-neutral-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-4">Hours</h3>
                  <BusinessHoursCollapsible businessHours={businessHours} isRetailStore={true} compact />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Full-width provenance callout — the conversion pitch */}
        <div className="max-w-6xl mx-auto px-6 -mt-8 relative z-20">
          <section className="bg-white rounded-xl p-6 border border-neutral-200 shadow-sm">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
              <div className="lg:col-span-2">
                <h2 className="text-2xl font-bold text-neutral-900 mb-3">About this listing</h2>
                {publicNarrative && (
                  <p className="text-neutral-700 leading-relaxed whitespace-pre-line">
                    {publicNarrative}
                  </p>
                )}
                {!publicNarrative && listing.description && (
                  <p className="text-neutral-700 leading-relaxed">
                    {listing.description}
                  </p>
                )}
                {!publicNarrative && !listing.description && !listing.isClaimed && (
                  <p className="text-neutral-600 leading-relaxed">
                    {disclaimer}
                  </p>
                )}
                {!listing.isClaimed && (
                  <p className="text-neutral-500 leading-relaxed mt-3">
                    Free to claim — verify your details, update hours, add a photo,
                    and showcase up to 5 products for nearby shoppers.
                  </p>
                )}
              </div>
              <div className="flex flex-col items-center lg:items-end gap-3">
                {listing.isClaimed ? null : hasClaimToken ? (
                  <a
                    href={claimHref}
                    onClick={trackClaimClick}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-semibold w-full lg:w-auto justify-center"
                  >
                    <ShieldCheck className="w-5 h-5" /> Claim this listing
                  </a>
                ) : (
                  <a
                    href={claimHref}
                    onClick={trackClaimClick}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-semibold w-full lg:w-auto justify-center"
                  >
                    <Info className="w-5 h-5" /> Are you the owner?
                  </a>
                )}
                <Link
                  href="/place/about"
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline mt-1"
                >
                  Learn what VisibleShelf offers
                </Link>
                {showsQr && (
                  <div className="flex flex-col items-center">
                    <TenantQRCode
                      url={currentUrl}
                      tenantId={listing.tenantId}
                      label="Scan to Share"
                      downloadName={listing.businessName?.toLowerCase().replace(/[^a-z0-9]/g, '-')}
                      size={120}
                      showDownload={true}
                      pageType="directory"
                      isPublic
                      hideBadges
                    />
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>

        {/* Product slot teaser — shows what claiming unlocks (E4) */}
        <div className="max-w-6xl mx-auto px-6 mb-6">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 rounded-xl p-8 border border-blue-100 dark:border-blue-900/30">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium mb-4">
                  <Tag className="w-3 h-3" />
                  Free With Your Claimed Listing
                </div>
                <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-2">
                  Put your 5 most-requested items online.
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                  Pick the 5 signature products customers constantly call to ask about — the
                  specialty items chain supermarkets never stock. When nearby shoppers search
                  for them, your store appears with actual prices and an{' '}
                  <strong>&quot;In-Stock at Store&quot;</strong> badge, pointing them straight
                  to your register.
                </p>
                <Link
                  href="/place/about"
                  className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 underline mt-3"
                >
                  See how your shelf gets found <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              <div className="flex justify-center">
                <ShelfSlotsPreview
                  className="w-full max-w-sm"
                  items={mapShelfItems(listing.shelfItems)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Market Intel — the "How We Found" card shows above the collapsed
            panel as its teaser, so the 5-products offer, the report card,
            and the intel panel form a mini funnel. Seed pages only. */}
        {listing.listingOrigin === 'directory_seed' && !listing.isClaimed && (
          <div className="max-w-6xl mx-auto px-6 mb-6">
            <MarketIntelSidebar
              slug={slug}
              initialTeaser={marketIntelTeaser}
              activeClaimToken={claimToken}
              seedId={listing.seedId}
              reportShortCode={listing.claimShortCode}
              qrStyle={listing.qrStyle}
              logoUrl={listing.logoUrl}
            />
          </div>
        )}

        {/* Claim inquiry form — shown when no claim token has been minted.
            Uses the same anonymous PublicInquiryForm pattern as storefront contact,
            routed to the platform tenant so the operator can mint a claim token. */}
        {!hasClaimToken && !listing.isClaimed && (
          <div id="claim-inquiry" className="max-w-3xl mx-auto px-6 py-12 scroll-mt-8">
            <div className="bg-white rounded-xl p-8 border border-neutral-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-neutral-900">Are you the business owner?</h2>
                  <p className="text-sm text-neutral-500">
                    Send us a message and we&apos;ll help you claim this listing for free.
                  </p>
                </div>
              </div>
              <PublicInquiryForm
                tenantId="platform"
                tenantName="VisibleShelf"
                sourceLabel="Place Claim Request"
                sourceTag="place_claim_request"
                listingId={listing.id}
                defaultSubject={`Claim request — ${listing.businessName}`}
                collapsedTitle="Request claim access"
                collapsedSubtitle="Tell us who you are and whether you want to claim this listing or have it removed."
                requestFields
                showFaqs={false}
              />
            </div>
          </div>
        )}

        {(listing.primaryCategory || (listing.city && listing.state)) && (
          <div className="bg-white border-t">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
              <h2 className="text-2xl font-bold text-neutral-900 mb-3">
                Browse more places
              </h2>
              <p className="text-neutral-600 mb-8">
                More local businesses listed from public information — by category or city.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                {listing.primaryCategory && (
                  <Link
                    href={getCategoryUrl({ name: listing.primaryCategory }, '/place/category')}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-full text-sm font-medium text-blue-700 transition-colors"
                  >
                    <Tag className="w-4 h-4" />
                    {listing.primaryCategory}
                    <span aria-hidden="true">→</span>
                  </Link>
                )}
                {secondaryShelves?.map(
                  (shelf) =>
                    shelf.href && (
                      <Link
                        key={shelf.label}
                        href={shelf.href}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 rounded-full text-sm font-medium text-neutral-700 transition-colors"
                      >
                        <Tag className="w-4 h-4" />
                        {shelf.label}
                        <span aria-hidden="true">→</span>
                      </Link>
                    ),
                )}
                {listing.city && listing.state && (
                  <Link
                    href={getPlaceCityShelfUrl(listing.city, listing.state)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-50 hover:bg-green-100 border border-green-200 rounded-full text-sm font-medium text-green-700 transition-colors"
                  >
                    <MapPin className="w-4 h-4" />
                    Places in {listing.city}
                    <span aria-hidden="true">→</span>
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        <ChainLocationsStrip locations={listing.chainLocations ?? []} />
        <RelatedStores currentSlug={slugForRelated} limit={3} title="Similar Places" />
        <LastViewed />
        <div className="container mx-auto px-4 py-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <AddBusinessCta
            category={listing.primaryCategory || listing.category}
            city={listing.city}
            state={listing.state}
            source={currentUrl}
          />
          <SuggestBusinessCta
            category={listing.primaryCategory || listing.category}
            city={listing.city}
            state={listing.state}
            source={currentUrl}
          />
        </div>
      </div>
    </>
  );
}
